import {
  Minus,
  Plus,
  Trash,
} from "@lazarentertainment/web-design-system/components/iconOutline"
import {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react"
import { useMergeRefs } from "rooks"

import { Button } from "./Button"

export const numberInputVariants = ["default", "compact"] as const

export type NumberInputProps = ComponentPropsWithoutRef<"input"> & {
  label: string
  variant?: (typeof numberInputVariants)[number]
  showTrashIconOnOneRemaining?: boolean

  /**
   * Because of our custom increment/decrement buttons, we're using a custom handleChange function.
   * Use this instead of `onChange` or `onInput` if you want to use NumberInput as a controlled input.
   */
  handleChange?: (newValue: string) => void
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      label,
      variant = "default",
      showTrashIconOnOneRemaining = false,
      handleChange,
      ...props
    }: NumberInputProps,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const refs = useMergeRefs(inputRef, ref)
    const [showTrashIcon, setShowTrashIcon] = useState(
      showTrashIconOnOneRemaining && props.defaultValue === "1",
    )

    const updateTrashIconIfNecessary = () => {
      setShowTrashIcon(
        showTrashIconOnOneRemaining && inputRef.current.value === "1",
      )
    }

    useEffect(() => {
      updateTrashIconIfNecessary()
    }, [showTrashIconOnOneRemaining])

    const onChange: ChangeEventHandler<HTMLInputElement> = () => {
      handleChange?.(inputRef.current?.value)
      updateTrashIconIfNecessary()
    }

    const increment = () => {
      inputRef.current?.stepUp()
      handleChange?.(inputRef.current?.value)
      updateTrashIconIfNecessary()
    }

    const decrement = () => {
      inputRef.current?.stepDown()
      handleChange?.(inputRef.current?.value)
      updateTrashIconIfNecessary()
    }

    const innerNumberInput = (
      <div className="number-input-number-section">
        {variant === "compact" && (
          <label
            className="screen-reader-only"
            htmlFor={`number-input-${label}`}
          >
            {label}
          </label>
        )}
        <Button
          type="button"
          onClick={decrement}
          size="sm"
          iconOnly
          icon={showTrashIcon ? Trash : Minus}
          variant="tertiary-gray"
          disabled={props.disabled}
        />
        <input
          ref={refs}
          id={`number-input-${label}`}
          className="number-input body-md-strong"
          type="number"
          onChange={onChange}
          disabled={props.disabled}
          {...props}
        />
        <Button
          type="button"
          onClick={increment}
          size="sm"
          iconOnly
          icon={Plus}
          variant="tertiary-gray"
          disabled={props.disabled}
        />
      </div>
    )

    return variant === "compact" ? (
      innerNumberInput
    ) : (
      <div
        className="input-container"
        aria-invalid={props["aria-invalid"]}
        {...(props.disabled ? { "data-disabled": true } : {})}
      >
        <label
          className="input-label title-sm"
          htmlFor={`number-input-${label}`}
        >
          {label}
        </label>
        {innerNumberInput}
      </div>
    )
  },
)

export default NumberInput
